import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import SlotifyLogo from "@/components/SlotifyLogo";
import { isDisposableEmail } from "@/lib/disposable-emails";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signUp, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error(t("auth.accept_terms_error"));
      return;
    }

    setLoading(true);

    const disposable = await isDisposableEmail(email);
    if (disposable) {
      setLoading(false);
      toast.error(t("auth.disposable_email_error") !== "auth.disposable_email_error"
        ? t("auth.disposable_email_error")
        : "Disposable email addresses are not allowed. Please use a permanent email.");
      return;
    }

    const { error } = await signUp(email, password, firstName, lastName, language);
    setLoading(false);
    if (error) {
      toast.error(t(error.message) !== error.message ? t(error.message) : error.message);
    } else {
      navigate("/verify-email");
    }
  };

  return (
    <div className="flex min-h-screen relative">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>
      <div className="hidden w-1/2 bg-gradient-hero lg:block">
        <div className="flex h-full flex-col items-center justify-center p-12">
          <div className="mb-8">
            <SlotifyLogo className="h-10 brightness-0 invert" />
          </div>
          <h2 className="text-2xl font-bold text-primary-foreground text-center">
            {t("auth.start_free")}
          </h2>
          <p className="mt-2 text-primary-foreground/60 text-center max-w-sm">
            {t("auth.start_free_desc")}
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <SlotifyLogo className="h-8" />
          </div>

          <h1 className="text-2xl font-bold text-foreground">{t("auth.sign_up")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("auth.sign_up_desc")}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t("auth.first_name")}</Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t("auth.last_name")}</Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                {t("auth.accept_terms_pre")}{" "}
                <Link to="/terms" className="font-medium text-primary hover:underline" target="_blank">
                  {t("auth.terms_link")}
                </Link>{" "}
                {t("auth.accept_terms_and")}{" "}
                <Link to="/privacy" className="font-medium text-primary hover:underline" target="_blank">
                  {t("auth.privacy_link")}
                </Link>
              </label>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("auth.creating") : t("auth.sign_up")}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              {t("auth.sign_in")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
